/**
 * Frame boxes for photos, video, and titles. The monitor overlay and the
 * compositor both use this so a drag, a font size, and an export agree.
 */

import type { CaptionClip, ClipPlacement, ImageCrop, TextClipStyle, TimelineClip, VideoProject } from '@canvas/shared-types';
import { fitRect, type Size } from './frameGeometry';
import { isClipProject } from './timelineEdit';

export const FONT_SIZE_CHOICES = [12, 16, 20, 24, 32, 40, 48, 60, 72, 96, 128, 160, 200];

export const PIP_CORNERS = {
  'top-left': { x: 0.2, y: 0.18, width: 0.34 },
  'top-right': { x: 0.8, y: 0.18, width: 0.34 },
  'bottom-left': { x: 0.2, y: 0.82, width: 0.34 },
  'bottom-right': { x: 0.8, y: 0.82, width: 0.34 },
} as const;

export type PipCorner = keyof typeof PIP_CORNERS;

export interface FrameBox {
  /** Top-left in frame pixels, before rotation. */
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  flipX: boolean;
  flipY: boolean;
}

export interface TextLayout {
  box: FrameBox;
  fontPx: number;
  lineHeight: number;
  lines: string[];
  /** CSS font string, without the size already baked in. */
  font: string;
  bar: boolean;
  look?: TextClipStyle['look'];
  caret?: boolean;
  fill: string;
  align: TextClipStyle['align'];
  background?: string;
}

export type TextMeasure = (text: string, font: string) => number;

export function fontPx(fontSize: number, frameHeight: number): number {
  return Math.max(8, (fontSize / 1080) * frameHeight);
}

export function clampFontSize(size: number): number {
  return Math.max(8, Math.min(400, Math.round(size)));
}

const FULL_CROP: ImageCrop = { x: 0, y: 0, width: 1, height: 1 };

export function sourceCrop(crop: ImageCrop | undefined): ImageCrop {
  const c = crop ?? FULL_CROP;
  const x = clamp01(c.x);
  const y = clamp01(c.y);
  const width = Math.max(0.02, Math.min(1 - x, c.width));
  const height = Math.max(0.02, Math.min(1 - y, c.height));
  return {
    x,
    y,
    width,
    height,
  };
}

export function sourcePixels(crop: ImageCrop | undefined, pixelWidth: number, pixelHeight: number): { x: number; y: number; width: number; height: number } {
  const c = sourceCrop(crop);
  return {
    x: c.x * pixelWidth,
    y: c.y * pixelHeight,
    width: Math.max(1, c.width * pixelWidth),
    height: Math.max(1, c.height * pixelHeight),
  };
}

export function mediaContentSize(clip: TimelineClip): Size {
  if (clip.kind === 'image' && clip.image) {
    const src = sourcePixels(clip.image.crop, Math.max(1, clip.image.width), Math.max(1, clip.image.height));
    return { width: src.width, height: src.height };
  }
  if (clip.kind === 'video' && clip.video) {
    return {
      width: Math.max(1, clip.video.naturalWidth || 16),
      height: Math.max(1, clip.video.naturalHeight || 9),
    };
  }
  return { width: 1, height: 1 };
}

/** Placement that draws the same rectangle as the legacy cover/contain fit. */
export function placementForFit(content: Size, frame: Size, fit: 'cover' | 'contain'): ClipPlacement {
  const rect = fitRect(content, frame, fit);
  return {
    x: (rect.x + rect.width / 2) / Math.max(1, frame.width),
    y: (rect.y + rect.height / 2) / Math.max(1, frame.height),
    width: rect.width / Math.max(1, frame.width),
    rotation: 0,
    flipX: false,
    flipY: false,
    lockAspect: true,
  };
}

export function resolvedPlacement(clip: TimelineClip, frame: Size, content: Size): ClipPlacement {
  if (clip.placement) return { ...clip.placement, lockAspect: clip.placement.lockAspect !== false };
  const fit = clip.image?.fit ?? 'cover';
  return placementForFit(content, frame, fit);
}

export function boxFromPlacement(placement: ClipPlacement, frame: Size, content: Size): FrameBox {
  const width = Math.max(8, placement.width * frame.width);
  const aspect = content.height / Math.max(1, content.width);
  const height = placement.lockAspect === false && placement.height
    ? Math.max(8, placement.height * frame.height)
    : Math.max(8, width * aspect);
  return {
    x: placement.x * frame.width - width / 2,
    y: placement.y * frame.height - height / 2,
    width,
    height,
    rotation: placement.rotation || 0,
    flipX: Boolean(placement.flipX),
    flipY: Boolean(placement.flipY),
  };
}

export function mediaBox(clip: TimelineClip, frame: Size): FrameBox | null {
  if (clip.kind !== 'image' && clip.kind !== 'video') return null;
  const content = mediaContentSize(clip);
  return boxFromPlacement(resolvedPlacement(clip, frame, content), frame, content);
}

function wrapParagraph(text: string, maxWidth: number | undefined, measure: (line: string) => number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  if (!maxWidth || maxWidth <= 0) return [words.join(' ')];
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (!line || measure(next) <= maxWidth) line = next;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

export function layoutText(
  style: TextClipStyle,
  placement: ClipPlacement | undefined,
  frame: Size,
  measure: TextMeasure
): TextLayout {
  const px = fontPx(style.fontSize, frame.height);
  const font = `${style.fontWeight} ${px}px ${style.fontFamily}`;
  const lineHeight = px * 1.15;
  const maxWidth = placement ? Math.max(px, placement.width * frame.width) : undefined;
  const paragraphs = (style.text || '').split('\n');
  const lines = paragraphs.flatMap((paragraph) => wrapParagraph(paragraph, maxWidth, (line) => measure(line, font)));
  const widths = lines.map((line) => measure(line || ' ', font));
  const textWidth = Math.max(px, ...widths, 0);
  const width = Math.max(textWidth, maxWidth ?? 0);
  const look = style.look;
  const bar = Boolean((style.bar && style.background && !style.fullFrame) || look === 'box' || look === 'button');
  let padX = bar ? px * (look === 'button' ? 0.85 : 0.5) : 0;
  let padY = bar ? px * (look === 'button' ? 0.42 : 0.32) : 0;
  if (look === 'circular') {
    padX = Math.max(padX, px * 0.9);
    padY = Math.max(padY, px * 0.9);
  }
  if (look === 'creator' || look === 'bubble' || look === 'retro' || look === 'pride') {
    padX += px * 0.22;
    padY += px * 0.22;
  }
  const boxWidth = width + padX * 2;
  const boxHeight = Math.max(lineHeight, lines.length * lineHeight) + padY * 2;
  const anchorX = (placement?.x ?? style.x) * frame.width;
  const anchorY = (placement?.y ?? style.y) * frame.height;
  let cx = anchorX;
  const cy = anchorY;
  if (!placement) {
    if (style.align === 'left') cx = anchorX + boxWidth / 2;
    if (style.align === 'right') cx = anchorX - boxWidth / 2;
  }
  return {
    fontPx: px,
    lineHeight,
    lines,
    font,
    bar,
    look,
    fill: style.fill,
    align: style.align,
    background: style.background,
    box: {
      x: cx - boxWidth / 2,
      y: cy - boxHeight / 2,
      width: boxWidth,
      height: boxHeight,
      rotation: placement?.rotation ?? 0,
      flipX: Boolean(placement?.flipX),
      flipY: Boolean(placement?.flipY),
    },
  };
}

/**
 * Apply a toolbar edit to a caption. A new phrase drops timed words, which
 * would otherwise keep painting the old line over the edited text.
 */
export function mergeCaptionEdit(
  caption: CaptionClip,
  patch: Partial<Pick<CaptionClip, 'text' | 'fill' | 'fontFamily' | 'fontSize'>>
): CaptionClip {
  const next: CaptionClip = {
    ...caption,
    text: patch.text ?? caption.text,
    fill: patch.fill !== undefined ? patch.fill : caption.fill,
    fontFamily: patch.fontFamily !== undefined ? patch.fontFamily : caption.fontFamily,
    fontSize: patch.fontSize !== undefined ? patch.fontSize : caption.fontSize,
  };
  if (patch.text !== undefined && patch.text !== caption.text) delete next.words;
  return next;
}

export function captionStyle(clip: TimelineClip, localMs: number): TextClipStyle | null {
  const caption = clip.caption;
  if (!caption) return null;
  const spoken = caption.words?.filter((word) => localMs >= word.startMs && localMs < word.endMs);
  const spokenText = spoken && spoken.length ? spoken.map((word) => word.text).join(' ') : '';
  const text = caption.text || spokenText;
  return {
    text,
    fontFamily: caption.fontFamily || 'Arial, sans-serif',
    fontSize: caption.fontSize || 42,
    fontWeight: 700,
    fill: caption.fill || '#ffffff',
    align: 'center',
    x: clip.placement?.x ?? 0.5,
    y: clip.placement?.y ?? 0.86,
    background: 'rgba(0,0,0,0.62)',
    bar: true,
  };
}

export function clipBox(clip: TimelineClip, frame: Size, measure: TextMeasure, localMs = 0): FrameBox | null {
  if (clip.kind === 'text' && clip.text) return layoutText(clip.text, clip.placement, frame, measure).box;
  if (clip.kind === 'caption') {
    const style = captionStyle(clip, localMs);
    if (!style) return null;
    return layoutText(style, clip.placement, frame, measure).box;
  }
  return mediaBox(clip, frame);
}

export function pointInBox(px: number, py: number, box: FrameBox): boolean {
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  const rad = (-box.rotation * Math.PI) / 180;
  const dx = px - cx;
  const dy = py - cy;
  const lx = dx * Math.cos(rad) - dy * Math.sin(rad);
  const ly = dx * Math.sin(rad) + dy * Math.cos(rad);
  return Math.abs(lx) <= box.width / 2 && Math.abs(ly) <= box.height / 2;
}

export function topClipAt(
  video: VideoProject,
  ms: number,
  frame: Size,
  px: number,
  py: number,
  measure: TextMeasure
): TimelineClip | null {
  if (!isClipProject(video)) return null;
  const tracks = video.tracks
    .filter((track) => track.kind !== 'audio' && !track.hidden)
    .slice()
    .sort((a, b) => b.index - a.index);
  for (const track of tracks) {
    const clips = video.clips
      .filter((clip) => clip.trackId === track.id && clip.kind !== 'audio' && clip.kind !== 'scene' && ms >= clip.startMs && ms < clip.startMs + clip.durationMs)
      .sort((a, b) => (b.z ?? 0) - (a.z ?? 0));
    for (const clip of clips) {
      const box = clipBox(clip, frame, measure, ms - clip.startMs);
      if (box && pointInBox(px, py, box)) return clip;
    }
  }
  return null;
}

export function movePlacement(placement: ClipPlacement, dx: number, dy: number, frame: Size): ClipPlacement {
  return {
    ...placement,
    x: placement.x + dx / Math.max(1, frame.width),
    y: placement.y + dy / Math.max(1, frame.height),
  };
}

export function scalePlacement(placement: ClipPlacement, factor: number): ClipPlacement {
  const width = clamp(placement.width * factor, 0.04, 4);
  if (placement.lockAspect === false && placement.height) {
    return { ...placement, width, height: clamp(placement.height * factor, 0.04, 4) };
  }
  return { ...placement, width };
}

export function scaleFont(fontSize: number, factor: number): number {
  return clampFontSize(fontSize * factor);
}

export function rotatePlacement(placement: ClipPlacement, deltaDeg: number): ClipPlacement {
  const rotation = (((placement.rotation || 0) + deltaDeg) % 360 + 360) % 360;
  return { ...placement, rotation };
}

export function flipPlacement(placement: ClipPlacement, axis: 'x' | 'y'): ClipPlacement {
  return axis === 'x' ? { ...placement, flipX: !placement.flipX } : { ...placement, flipY: !placement.flipY };
}

export function pipPlacement(placement: ClipPlacement, corner: PipCorner): ClipPlacement {
  return { ...placement, ...PIP_CORNERS[corner], height: undefined, lockAspect: true, rotation: placement.rotation || 0 };
}

export function placementFromBox(box: FrameBox, frame: Size, base?: ClipPlacement): ClipPlacement {
  return {
    x: (box.x + box.width / 2) / Math.max(1, frame.width),
    y: (box.y + box.height / 2) / Math.max(1, frame.height),
    width: box.width / Math.max(1, frame.width),
    height: base?.lockAspect === false ? box.height / Math.max(1, frame.height) : undefined,
    rotation: box.rotation,
    flipX: box.flipX,
    flipY: box.flipY,
    lockAspect: base?.lockAspect !== false,
  };
}

/** Crop by moving one edge. `delta` is a fraction of the source along that axis. */
export function insetCrop(crop: ImageCrop | undefined, edge: 'left' | 'right' | 'top' | 'bottom', delta: number): ImageCrop {
  const current = { ...(crop ?? FULL_CROP) };
  if (edge === 'left') {
    const x = clamp(current.x + delta, 0, current.x + current.width - 0.08);
    current.width = current.x + current.width - x;
    current.x = x;
  } else if (edge === 'right') {
    current.width = clamp(current.width + delta, 0.08, 1 - current.x);
  } else if (edge === 'top') {
    const y = clamp(current.y + delta, 0, current.y + current.height - 0.08);
    current.height = current.y + current.height - y;
    current.y = y;
  } else {
    current.height = clamp(current.height + delta, 0.08, 1 - current.y);
  }
  return current;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function clamp01(n: number): number {
  return clamp(n, 0, 1);
}
